import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { authApi, ApiError } from '../lib/api';
import { useTheme } from '../context/ThemeContext';

export default function ResetPassword() {
  const [email, setEmail] = useState('');
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const { theme, toggleTheme } = useTheme();

  const resetMutation = useMutation({
    mutationFn: () => authApi.resetPasswordRequest(email),
    onSuccess: () => {
      setSuccess(true);
      setError('');
    },
    onError: (err) => {
      if (err instanceof ApiError) setError(err.message);
      else setError('リセットに失敗しました');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    resetMutation.mutate();
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-theme-main px-4 transition-colors duration-300">
      {/* テーマ切り替えボタン */}
      <button
        onClick={toggleTheme}
        className="fixed top-4 right-4 p-3 bg-theme-card border border-theme rounded-lg hover:bg-theme-hover transition-colors"
        title={theme === 'dark' ? 'ライトモード' : 'ダークモード'}
      >
        {theme === 'dark' ? '☀️' : '🌙'}
      </button>

      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="text-6xl mb-4">🤖</div>
          <h1 className="text-3xl font-bold text-accent">Jarvis</h1>
          <p className="text-theme-secondary mt-2">パスワードリセット</p>
        </div>

        <div className="bg-theme-card rounded-xl p-8 border border-theme shadow-lg transition-colors duration-300">
          {success ? (
            <div className="text-center">
              <div className="text-4xl mb-4">✉️</div>
              <h2 className="text-xl font-bold text-theme-primary mb-4">
                メールを送信しました
              </h2>
              <p className="text-theme-secondary mb-6">
                パスワードリセット用のリンクをメールで送信しました。
                メールに記載されたリンクからパスワードを再設定してください。
              </p>
              <Link to="/login" className="text-accent hover:underline">
                ログインページへ戻る
              </Link>
            </div>
          ) : (
            <>
              <p className="text-theme-secondary mb-6 text-center">
                登録されているメールアドレスを入力してください。
                パスワードリセット用のリンクをお送りします。
              </p>

              {error && (
                <div className="mb-6 p-4 bg-red-500 bg-opacity-20 border border-red-500 rounded-lg text-red-500 text-sm">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-theme-secondary mb-2">
                    メールアドレス
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="w-full px-4 py-3 bg-theme-input border border-theme rounded-lg text-theme-primary placeholder-theme-muted focus:outline-none focus:ring-2 focus:ring-accent transition-colors"
                    placeholder="your@email.com"
                  />
                </div>

                <button
                  type="submit"
                  disabled={resetMutation.isPending}
                  className="w-full py-3 bg-accent text-navy-900 font-bold rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50"
                >
                  {resetMutation.isPending ? '送信中...' : 'リセットリンクを送信'}
                </button>
              </form>

              <div className="mt-6 text-center">
                <Link to="/login" className="text-accent hover:underline text-sm">
                  ログインページへ戻る
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
